import { Field, FastField, FieldArray } from "formik";
import * as formsService from "../../../services/formsService";
import React, { useEffect, useMemo, useState } from "react";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import { InputField, PhoneInputField, SocialSecurityInputField, AddAdditional } from "@coverme/forms";
import { DateInput } from "../../../components/FormComponents/DateInput";
import { SimpleSingleSelect } from "../../../components/FormComponents/SimpleSingleSelect";
import { YesNoButtons, YesNoButtonsInverted } from "../../../components/FormComponents/YesNoButtons";
import { getAge, getCustomFormattedNameFromObject } from "../../../services/stringService";
import { PhoneTypeSelect } from "../../../components/FormComponents/PhoneTypeSelect";
import { InterviewPersonDocumentation } from "../../../components/FormComponents/InterviewPersonDocumentation";
import { WizardButtonsField } from "../Components/WizardButtons";
import PregnancyFields from "../Components/PregnancyFields";
import { TooltipMessage, TooltipTrigger } from "@coverme/elements";
import { Icon } from "@coverme/icons";
import { Tooltip } from "../../../components/Tooltips/Tooltip";
import AddressFields from "../../../components/FormComponents/AddressFields";
import deleteIconSrc from "../../../resources/delete-icon.svg";
import "./PersonalForm.scss";

export const PersonalForm = props => {
    const {
        setFieldValue,
        handleSubmit,
        dirty,
        isValid,
        errors,
        touched,
        values,
        setFieldTouched,
        counties,
        onError,
        lookups,
        customerJurisdictions,
        navigateToNextPage,
        encounterStatus,
        encounterSource,
        documentTypes,
        documents,
        isLoading,
        isRequired = () => false,
        submitCount,
    } = props;
    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
    const getErrors = formsService.getErrors.bind(this, errors);

    const [personTobeVerified, setPersonTobeVerified] = useState(undefined);
    const [idVerificationResult, setIdVerificationResult] = useState(undefined);
    const [addressVerificationResult, setAddressVerificationResult] = useState(undefined);
    const [residencyVerificationResult, setResidencyVerificationResult] = useState(undefined);
    const [citizenshipVerificationResult, setCitizenshipVerificationResult] = useState(undefined);

    useEffect(() => {
        let idVerification = "";
        let addressVerification = "";
        let residencyVerification = "";
        let citizenshipVerification = "";

        let verifiedPerson = values.patient;

        if (values.isResponsiblePartyDifferentFromPatient === true) {
            verifiedPerson = values.responsibleParty;
        }
        setPersonTobeVerified({ ...verifiedPerson });

        idVerification = verifiedPerson.personVerificationModels?.filter(x => x.verificationType === "IdVerification");
        addressVerification = verifiedPerson.personVerificationModels?.filter(
            x => x.verificationType === "AddressVerification"
        );
        residencyVerification = verifiedPerson.personVerificationModels?.filter(
            x => x.verificationType === "ResidencyVerification"
        );
        citizenshipVerification = verifiedPerson.personVerificationModels?.filter(
            x => x.verificationType === "CitizenshipVerification"
        );
        personTobeVerified;
        setIdVerificationResult(
            idVerification !== null && idVerification !== undefined && idVerification.length > 0
                ? idVerification[0].verificationResult
                : null
        );

        setAddressVerificationResult(
            addressVerification !== null && addressVerification !== undefined && addressVerification.length > 0
                ? addressVerification[0].verificationResult
                : null
        );

        setResidencyVerificationResult(
            residencyVerification !== null && residencyVerification !== undefined && residencyVerification.length > 0
                ? residencyVerification[0].verificationResult
                : null
        );

        setCitizenshipVerificationResult(
            citizenshipVerification !== null &&
                citizenshipVerification !== undefined &&
                citizenshipVerification.length > 0
                ? citizenshipVerification[0].verificationResult
                : null
        );
    }, [values.isResponsiblePartyDifferentFromPatient, values.patient.isResponsibleParty, submitCount]);

    const patientCountiesForState = useMemo(
        () =>
            counties
                .filter(x => x.state === values.patient.address?.state)
                .map(x => ({
                    value: x.key,
                    label: x.name,
                })),
        [counties, values.patient.address]
    );

    const rpHasState = values.responsibleParty?.address?.state;

    const rpCountiesForState = useMemo(() => {
        if (values.responsibleParty?.address?.state == null) {
            return [];
        }

        return counties
            .filter(x => x.state === values.responsibleParty.address?.state)
            .map(x => ({
                value: x.key,
                label: x.name,
            }));
    }, [counties, values.responsibleParty]);

    function setPatientPhoneType() {
        if (!values.patient.primaryPhoneNumber.phoneType) {
            values.patient.primaryPhoneNumber.phoneType = "";
        }
    }

    function setRPPhoneType() {
        if (!values.responsibleParty.primaryPhoneNumber.phoneType) {
            values.responsibleParty.primaryPhoneNumber.phoneType = "";
        }
    }

    return (
        <form className="wizard-form">
            <OnSubmitValidationError callback={onError} />
            <div className="form-section">
                <div className="form-control m-input">
                    <FastField
                        name="patient.mrn"
                        render={({ field }) => (
                            <InputField
                                id={field.name}
                                title={"Medical Record Number"}
                                placeholder={"MRN"}
                                errorMessage={getErrors(field)}
                                required={isRequired(field)}
                                validState={getValidationState(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
                <div className="form-control s-160-input">
                    <Field
                        name="serviceStartDate"
                        render={({ field }) => (
                            <DateInput
                                {...field}
                                id="serviceStartDate"
                                title={"Service Start Date"}
                                changeEvent={v => setFieldValue(field.name, v)}
                                errorMessage={getErrors(field)}
                                required={isRequired(field)}
                                validState={getValidationState(field)}
                            />
                        )}
                    />
                </div>
                <div className="form-control s-160-input">
                    <Field
                        name="serviceEndDate"
                        render={({ field }) => (
                            <DateInput
                                {...field}
                                id="serviceEndDate"
                                title={"Service End Date"}
                                changeEvent={v => setFieldValue(field.name, v)}
                                errorMessage={getErrors(field)}
                                required={isRequired(field)}
                                validState={getValidationState(field)}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-control m-input">
                    <FastField
                        name="patient.firstName"
                        render={({ field }) => (
                            <InputField
                                id="patient.firstName"
                                title={"First Name"}
                                placeholder={"First Name"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
                <div className="form-control xs-input">
                    <FastField
                        name="patient.middleInitial"
                        render={({ field }) => (
                            <InputField
                                id="patient.middleInitial"
                                title={"MI"}
                                maxLength={1}
                                placeholder={"MI"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
                <div className="form-control m-input">
                    <FastField
                        name="patient.lastName"
                        render={({ field }) => (
                            <InputField
                                id="patient.lastName"
                                title={"Last Name"}
                                placeholder={"Last Name"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
                <div className="form-control s-input">
                    <FastField
                        name="patient.suffix"
                        render={({ field }) => (
                            <InputField
                                id="patient.suffix"
                                title={"Suffix"}
                                placeholder={"Suffix"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                maxLength={5}
                                {...field}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="form-control s-140-input no-upscope">
                <FastField
                    name="patient.ssn"
                    render={({ field }) => (
                        <SocialSecurityInputField
                            {...field}
                            id="patient.ssn"
                            title={"SSN"}
                            value={field.value}
                            changeEvent={e => {
                                setFieldValue(field.name, e.substring(0, 9));
                            }}
                            errorMessage={getErrors(field)}
                            required={isRequired(field)}
                            validState={getValidationState(field)}
                        />
                    )}
                />
            </div>
            <div className="form-control s-160-input">
                <Field
                    name="patient.dob"
                    render={({ field }) => (
                        <DateInput
                            {...field}
                            id="patient.dob"
                            title={"Date of Birth"}
                            changeEvent={v => setFieldValue(field.name, v)}
                            errorMessage={getErrors(field)}
                            required={isRequired(field)}
                            validState={getValidationState(field)}
                            value={field.value}
                        />
                    )}
                />
            </div>
            <div className="form-control s-input">
                <Field
                    name="patient.gender"
                    render={({ field }) => (
                        <SimpleSingleSelect
                            {...field}
                            id={field.name}
                            title={"Gender"}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            required={isRequired(field)}
                            options={lookups.gender}
                            matchFromStart={true}
                        />
                    )}
                />
            </div>
            <div className="form-control xl-input">
                <FastField
                    name="patient.preferredLanguage"
                    render={({ field }) => (
                        <SimpleSingleSelect
                            {...field}
                            id={field.name}
                            title={"Language"}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                            errorMessage={getErrors(field)}
                            required={isRequired(field)}
                            validState={getValidationState(field)}
                            options={lookups.language}
                        />
                    )}
                />
            </div>
            <div id="ie-heirarchy-container" className="form-section">
                <AddressFields
                    baseFieldName="patient.address"
                    getErrors={getErrors}
                    getValidationState={getValidationState}
                    isRequired={isRequired}
                    states={lookups.states}
                    counties={counties}
                    showCounties={true}
                    customerJurisdictions={customerJurisdictions}
                />
            </div>
            <div className="form-section">
                {values.patient?.address?.state !== "" &&
                    customerJurisdictions.indexOf(values.patient?.address?.state) < 0 && (
                        <div className="form-control">
                            <YesNoButtons
                                name={"patient.isPlanningToMoveToJurisdiction"}
                                title={`Does ${getCustomFormattedNameFromObject(
                                    values.patient,
                                    "the patient"
                                )} intend to move to ${customerJurisdictions.join(" or ")}`}
                                getErrors={getErrors}
                                getValidationState={getValidationState}
                                isRequired={isRequired}
                                {...props}
                            />
                        </div>
                    )}
            </div>
            <div className="form-section">
                <div className="form-control">
                    <YesNoButtonsInverted
                        name={"patient.isMailingAddressDifferent"}
                        title={`Is ${getCustomFormattedNameFromObject(
                            values.patient,
                            "the patient"
                        )}'s mailing address the same?`}
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        isRequired={isRequired}
                        {...props}
                    />
                </div>
            </div>
            {values.patient.isMailingAddressDifferent && (
                <>
                    <h4>Mailing Address</h4>
                    <AddressFields
                        customerJurisdictions={customerJurisdictions}
                        baseFieldName="patient.mailingAddress"
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        isRequired={isRequired}
                        states={lookups.states}
                        counties={counties}
                        showCounties={false}
                    />
                </>
            )}
            <div className="form-section">
                <div className="form-control m-input">
                    <FastField
                        name="patient.primaryPhoneNumber.phoneNumber"
                        render={({ field }) => (
                            <PhoneInputField
                                {...field}
                                title={"Primary Phone Number"}
                                id="patient.phoneNumber"
                                value={field.value}
                                errorMessage={getErrors(field)}
                                required={isRequired(field)}
                                validState={getValidationState(field)}
                                changeEvent={e => {
                                    setFieldValue(field.name, e);
                                    setPatientPhoneType();
                                }}
                            />
                        )}
                    />
                </div>
                <div className="form-control s-input" >
                    <Field
                        name="patient.primaryPhoneNumber.phoneType"
                        render={({ field }) => (
                            <PhoneTypeSelect
                                {...field}
                                id={"patient.phoneType"}
                                setFieldTouched={setFieldTouched}
                                setFieldValue={setFieldValue}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                            />
                        )}
                    />
                </div>
            </div>
            <FieldArray
                name="patient.alternatePhoneNumbers"
                render={arrayHelpers => {
                    const phoneNumbers = values.patient.alternatePhoneNumbers;
                    return (
                        <>
                            {values.patient.alternatePhoneNumbers !== undefined &&
                                values.patient.alternatePhoneNumbers !== null &&
                                phoneNumbers.map((x, i) => (
                                    <div className="form-section" key={`patient.alternatePhoneNumbers.${i}`}>
                                        <div className="form-control s-160-input">
                                            <FastField
                                                name={`patient.alternatePhoneNumbers.${i}.phoneNumber`}
                                                render={({ field }) => (
                                                    <PhoneInputField
                                                        {...field}
                                                        title={"Phone Number"}
                                                        id={field.name}
                                                        value={field.value}
                                                        errorMessage={getErrors(field)}
                                                        required={isRequired(field)}
                                                        validState={getValidationState(field)}
                                                        changeEvent={e => {
                                                            setFieldValue(field.name, e);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div
                                            className="form-control s-input"
                                            style={{ zIndex: `${1900 - parseInt(i)}` }}
                                        >
                                            <Field
                                                name={`patient.alternatePhoneNumbers.${i}.phoneType`}
                                                render={({ field }) => (
                                                    <PhoneTypeSelect
                                                        {...field}
                                                        id={field}
                                                        setFieldTouched={setFieldTouched}
                                                        setFieldValue={setFieldValue}
                                                        errorMessage={getErrors(field)}
                                                        validState={getValidationState(field)}
                                                        required={isRequired(field)}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div
                                            className="form-control s-input center-aligned-icon"
                                            onClick={() => arrayHelpers.remove(i)}
                                        >
                                            <img src={deleteIconSrc} />
                                        </div>
                                    </div>
                                ))}
                            <AddAdditional
                                clickEvent={() => {
                                    arrayHelpers.push({
                                        phoneNumber: "",
                                        phoneType: "",
                                    });
                                }}
                                labelText="Add additional phone number"
                            />
                        </>
                    );
                }}
            />

            <div className="form-section">
                <div className="form-control l-input">
                    <FastField
                        name="patient.email"
                        render={({ field }) => (
                            <InputField
                                id="patient.email"
                                title={"Email"}
                                placeholder={"coverme@email.com"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
            </div>

            {encounterSource !== "Ocr" && encounterSource !== "Ocr Quick Entry" && (
                <div className="form-section">
                    <div className="form-control auto-input">
                        <YesNoButtons
                            name={"patient.doesHaveId"}
                            title={`Did ${getCustomFormattedNameFromObject(
                                values.patient,
                                "the patient "
                            )}  provide a form of identification?`}
                            onFieldChange={e => {
                                if (e === false) {
                                    setFieldValue("patient.documentInfo", {
                                        documentNumber: "",
                                        documentType: "",
                                        issuingState: "",
                                    });
                                }
                            }}
                            getErrors={getErrors}
                            getValidationState={getValidationState}
                            isRequired={isRequired}
                            {...props}
                        />
                    </div>
                    {values.patient.doesHaveId && (
                        <div className="form-section">
                            <InterviewPersonDocumentation
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                states={lookups.states}
                                getValidationState={getValidationState}
                                getErrors={getErrors}
                                values={values}
                                fieldBaseName={"patient.documentInfo"}
                                documentNumberName={"patient.documentInfo.documentNumber"}
                                documentTypeName={"patient.documentInfo.documentType"}
                                issuingStateName={"patient.documentInfo.issuingState"}
                                documentTypes={lookups.otherDocumentTypes}
                                isRequired={isRequired}
                                fullName={getCustomFormattedNameFromObject(values.patient, "the patient")}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="form-section">
                <div className="form-control auto-input">
                    <YesNoButtons
                        name={"patient.isMarried"}
                        title={`Is ${getCustomFormattedNameFromObject(values.patient, "the patient")}  married?`}
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        isRequired={isRequired}
                        {...props}
                    />
                </div>
            </div>

            {getAge(values.patient.dob) > 13 && values.patient.gender === "Female" && (
                <PregnancyFields
                    fullName={getCustomFormattedNameFromObject(values.patient, "the patient")}
                    isRequired={isRequired}
                    getErrors={getErrors}
                    getValidationState={getValidationState}
                    fieldSection="patient"
                />
            )}
            <div className="form-section">
                <div className="form-control auto-input">
                    <YesNoButtons
                        name={"patient.isUsCitizen"}
                        title={`Is ${getCustomFormattedNameFromObject(values.patient, "the patient")}  a US Citizen?`}
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        isRequired={isRequired}
                        {...props}
                    />
                </div>
                {values.patient.isUsCitizen === false && (
                    <div className="form-control l-input">
                        <Field
                            name="patient.usMovingDate"
                            render={({ field }) => (
                                <DateInput
                                    {...field}
                                    id="patient.usMovingDate"
                                    title={`Date ${getCustomFormattedNameFromObject(
                                        values.patient,
                                        "the patient"
                                    )}  began living in the US`}
                                    changeEvent={v => setFieldValue(field.name, v)}
                                    errorMessage={getErrors(field)}
                                    required={isRequired(field)}
                                    validState={getValidationState(field)}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
            {values.patient.isUsCitizen === false && (
                <div className="form-section">
                    <div className="form-control m-input">
                        <FastField
                            name="patient.documentIdType"
                            render={({ field }) => (
                                <InputField
                                    id="patient.documentIdType"
                                    title={"Document ID Type"}
                                    placeholder={"Document ID Type"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                    <div className="form-control xxxl-input">
                        <Field
                            name="patient.immigrationStatus"
                            render={({ field }) => (
                                <SimpleSingleSelect
                                    {...field}
                                    id={field.name}
                                    title={"Immigration Status"}
                                    setFieldTouched={setFieldTouched}
                                    setFieldValue={setFieldValue}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    options={lookups.immigrationStatus}
                                />
                            )}
                        />
                    </div>
                    <div className="form-control l-input">
                        <FastField
                            name="patient.documentIdNumber"
                            render={({ field }) => (
                                <InputField
                                    id="patient.documentIdNumber"
                                    title={"Document ID Number"}
                                    placeholder={"Document ID Number"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            {values.patient.isUsCitizen === false && (
                <div className="form-section">
                    <div className="form-control l-input">
                        <FastField
                            name="patient.alienOrI94Number"
                            render={({ field }) => (
                                <InputField
                                    id="patient.alienOrI94Number"
                                    title={"Alien or I-94 Number"}
                                    placeholder={"Alien or I-94 Number"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className="form-control xl-input">
                        <FastField
                            name="patient.cardNumberOrPassportNumber"
                            render={({ field }) => (
                                <InputField
                                    id="patient.cardNumberOrPassportNumber"
                                    title={"Card Number or Passport Number"}
                                    placeholder={"Card Number or Passport Number"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
            {values.patient.isUsCitizen === false && (
                <div className="form-section">
                    <div className="form-control l-input">
                        <FastField
                            name="patient.sevisIDOrExpirationDate"
                            render={({ field }) => (
                                <InputField
                                    id="patient.sevisIDOrExpirationDate"
                                    title={"SEVIS ID or Expiration Date"}
                                    placeholder={"SEVIS ID or Expiration Date"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className="form-control xxl-input">
                        <FastField
                            name="patient.other"
                            render={({ field }) => (
                                <InputField
                                    id="patient.other"
                                    title={"Other (category code or country of issuances)"}
                                    placeholder={"Other (category code or country of issuances)"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            <div className="form-section">
                <div className="form-control auto-input" />
            </div>

            <div className="form-section" id="is-responsible-party-different">
                <div className="form-control xxl-input has-tooltip">
                    <YesNoButtonsInverted
                        name={"isResponsiblePartyDifferentFromPatient"}
                        title={`Is ${getCustomFormattedNameFromObject(
                            values.patient,
                            "the patient"
                        )} the responsible party?`}
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        isRequired={isRequired}
                        {...props}
                    />
                    <Tooltip>
                        <TooltipTrigger>
                            <Icon icon={"tooltip"} />
                        </TooltipTrigger>
                        <TooltipMessage>
                            <div>{"The Responsible Party is the person who will pay the medical bill."}</div>
                        </TooltipMessage>
                    </Tooltip>
                </div>
            </div>

            {values.isResponsiblePartyDifferentFromPatient && (
                <>
                    <h4>Responsible Party</h4>
                    <div className="form-section">
                        <div className="form-control m-input">
                            <FastField
                                name="responsibleParty.firstName"
                                render={({ field }) => (
                                    <InputField
                                        id="responsibleParty.firstName"
                                        title={"First Name"}
                                        placeholder={"First Name"}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        required={isRequired(field)}
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="form-control xs-input">
                            <FastField
                                name="responsibleParty.middleInitial"
                                render={({ field }) => (
                                    <InputField
                                        id="responsibleParty.middleInitial"
                                        title={"MI"}
                                        placeholder={"MI"}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        required={isRequired(field)}
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="form-control m-input">
                            <FastField
                                name="responsibleParty.lastName"
                                render={({ field }) => (
                                    <InputField
                                        id="responsibleParty.lastName"
                                        title={"Last Name"}
                                        placeholder={"Last Name"}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        required={isRequired(field)}
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        <div className="form-control s-input">
                            <FastField
                                name="responsibleParty.suffix"
                                render={({ field }) => (
                                    <InputField
                                        id="responsibleParty.suffix"
                                        title={"Suffix"}
                                        placeholder={"Suffix"}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        required={isRequired(field)}
                                        maxLength={5}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="form-control s-140-input">
                        <FastField
                            name="responsibleParty.ssn"
                            render={({ field }) => (
                                <SocialSecurityInputField
                                    {...field}
                                    id="responsibleParty.ssn"
                                    title={"SSN"}
                                    value={field.value}
                                    changeEvent={e => {
                                        setFieldValue(field.name, e.substring(0, 9));
                                    }}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                />
                            )}
                        />
                    </div>
                    <div className="form-control s-160-input">
                        <Field
                            name="responsibleParty.dob"
                            render={({ field }) => (
                                <DateInput
                                    {...field}
                                    id="responsibleParty.dob"
                                    title={"Date of Birth"}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    changeEvent={e => {
                                        setFieldValue(field.name, e);
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="form-control s-140-input">
                        <Field
                            name="responsibleParty.gender"
                            render={({ field }) => (
                                <SimpleSingleSelect
                                    {...field}
                                    title={"Gender"}
                                    id={field.name}
                                    setFieldTouched={setFieldTouched}
                                    setFieldValue={setFieldValue}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    options={lookups.gender}
                                    matchFromStart={true}
                                />
                            )}
                        />
                    </div>
                    <div className="form-control xl-input">
                        <FastField
                            name="responsibleParty.preferredLanguage"
                            render={({ field }) => (
                                <SimpleSingleSelect
                                    {...field}
                                    id={field.name}
                                    title={"Language"}
                                    setFieldTouched={setFieldTouched}
                                    setFieldValue={setFieldValue}
                                    errorMessage={getErrors(field)}
                                    validState={getValidationState(field)}
                                    required={isRequired(field)}
                                    options={lookups.language}
                                />
                            )}
                        />
                    </div>
                    <div className="form-section">
                        <div className="form-control">
                            <YesNoButtons
                                name={"responsibleParty.doesLiveWithPatientForMoreThan6Months"}
                                title={`Does ${getCustomFormattedNameFromObject(
                                    values.responsibleParty,
                                    "the responsible party"
                                )} live with ${getCustomFormattedNameFromObject(values.patient) ||
                                    "the patient"} for more than six (6) months out of the year?`}
                                getErrors={getErrors}
                                isRequired={isRequired}
                                getValidationState={getValidationState}
                                onFieldChange={value => {
                                    if (value === true) {
                                        setFieldValue("responsibleParty.address.line1", values.patient.address?.line1);
                                        setFieldValue("responsibleParty.address.line2", values.patient.address?.line2);
                                        setFieldValue("responsibleParty.address.city", values.patient.address?.city);
                                        setFieldValue("responsibleParty.address.state", values.patient.address?.state);
                                        setFieldValue(
                                            "responsibleParty.address.county",
                                            values.patient.address?.county
                                        );
                                        setFieldValue(
                                            "responsibleParty.address.zipCode",
                                            values.patient.address?.zipCode
                                        );
                                    } else {
                                        setFieldValue("responsibleParty.address.line1", "");
                                        setFieldValue("responsibleParty.address.line2", "");
                                        setFieldValue("responsibleParty.address.city", "");
                                        setFieldValue("responsibleParty.address.state", "");
                                        setFieldValue("responsibleParty.address.county", "");
                                        setFieldValue("responsibleParty.address.zipCode", "");
                                    }
                                }}
                                {...props}
                            />
                        </div>
                    </div>
                    <AddressFields
                        baseFieldName="responsibleParty.address"
                        getErrors={getErrors}
                        getValidationState={getValidationState}
                        customerJurisdictions={customerJurisdictions}
                        isRequired={isRequired}
                        states={lookups.states}
                        counties={counties}
                        disabled={values.responsibleParty.doesLiveWithPatientForMoreThan6Months}
                        showCounties={true}
                    />
                    <div className="form-section">
                        <div className="form-control">
                            <YesNoButtonsInverted
                                name={"responsibleParty.isMailingAddressDifferent"}
                                title={`Is ${getCustomFormattedNameFromObject(
                                    values.responsibleParty,
                                    "the responsible party"
                                )}'s mailing address the same?`}
                                getErrors={getErrors}
                                getValidationState={getValidationState}
                                isRequired={isRequired}
                                {...props}
                            />
                        </div>
                    </div>
                    {values.responsibleParty.isMailingAddressDifferent && (
                        <>
                            <h4>Mailing Address</h4>
                            <AddressFields
                                baseFieldName="responsibleParty.mailingAddress"
                                getErrors={getErrors}
                                getValidationState={getValidationState}
                                customerJurisdictions={customerJurisdictions}
                                isRequired={isRequired}
                                states={lookups.states}
                                counties={counties}
                                showCounties={true}
                            />
                        </>
                    )}
                    <div className="form-section">
                        <div className="form-control m-input">
                            <FastField
                                name="responsibleParty.primaryPhoneNumber.phoneNumber"
                                render={({ field }) => (
                                    <PhoneInputField
                                        {...field}
                                        title={"Primary Phone Number"}
                                        id="responsibleParty.phoneNumber"
                                        value={field.value}
                                        errorMessage={getErrors(field)}
                                        required={isRequired(field)}
                                        validState={getValidationState(field)}
                                        changeEvent={e => {
                                            setFieldValue(field.name, e);
                                            setRPPhoneType();
                                        }}
                                    />
                                )}
                            />
                        </div>
                        <div className="form-control s-input" >
                            <Field
                                name="responsibleParty.primaryPhoneNumber.phoneType"
                                render={({ field }) => (
                                    <PhoneTypeSelect
                                        {...field}
                                        id={"responsibleParty.primaryPhoneNumber.phoneType"}
                                        setFieldTouched={setFieldTouched}
                                        setFieldValue={setFieldValue}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        required={isRequired(field)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <FieldArray
                        name="responsibleParty.alternatePhoneNumbers"
                        render={arrayHelpers => {
                            const phoneNumbers = values.responsibleParty.alternatePhoneNumbers;
                            return (
                                <>
                                    {values.responsibleParty.alternatePhoneNumbers !== undefined &&
                                        values.responsibleParty.alternatePhoneNumbers !== null &&
                                        phoneNumbers.map((x, i) => (
                                            <div
                                                className="form-section"
                                                key={`responsibleParty.alternatePhoneNumbers.${i}`}
                                            >
                                                <div className="form-control s-160-input">
                                                    <FastField
                                                        name={`responsibleParty.alternatePhoneNumbers.${i}.phoneNumber`}
                                                        render={({ field }) => (
                                                            <PhoneInputField
                                                                {...field}
                                                                title={"Phone Number"}
                                                                id={field.name}
                                                                value={field.value}
                                                                errorMessage={getErrors(field)}
                                                                required={isRequired(field)}
                                                                validState={getValidationState(field)}
                                                                changeEvent={e => {
                                                                    setFieldValue(field.name, e);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div
                                                    className="form-control s-input"
                                                    style={{ zIndex: `${1800 - parseInt(i)}` }}
                                                >
                                                    <Field
                                                        name={`responsibleParty.alternatePhoneNumbers.${i}.phoneType`}
                                                        render={({ field }) => (
                                                            <PhoneTypeSelect
                                                                {...field}
                                                                id={field.name}
                                                                setFieldTouched={setFieldTouched}
                                                                setFieldValue={setFieldValue}
                                                                errorMessage={getErrors(field)}
                                                                validState={getValidationState(field)}
                                                                required={isRequired(field)}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div
                                                    className="form-control s-input center-aligned-icon"
                                                    onClick={() => arrayHelpers.remove(i)}
                                                >
                                                    <img src={deleteIconSrc} />
                                                </div>
                                            </div>
                                        ))}
                                    <AddAdditional
                                        clickEvent={() => {
                                            arrayHelpers.push({
                                                phoneNumber: "",
                                                phoneType: "",
                                            });
                                        }}
                                        labelText="Add additional phone number"
                                    />
                                </>
                            );
                        }}
                    />
                    <div className="form-section">
                        <div className="form-control l-input">
                            <FastField
                                name="responsibleParty.email"
                                render={({ field }) => (
                                    <InputField
                                        id="responsibleParty.email"
                                        title={"Email"}
                                        placeholder={"coverme@email.com"}
                                        errorMessage={getErrors(field)}
                                        validState={getValidationState(field)}
                                        {...field}
                                    />
                                )}
                            />
                        </div>

                        {encounterSource !== "Ocr" && encounterSource !== "Ocr Quick Entry" && (
                            <div className="form-section">
                                <div className="form-control auto-input">
                                    <YesNoButtons
                                        name={"responsibleParty.doesHaveId"}
                                        title={`Did ${getCustomFormattedNameFromObject(
                                            values.responsibleParty,
                                            "the responsible party "
                                        )}  provide a form of identification?`}
                                        onFieldChange={e => {
                                            if (e === false) {
                                                setFieldValue("responsibleParty.documentInfo", {
                                                    documentNumber: "",
                                                    documentType: "",
                                                    issuingState: "",
                                                });
                                            }
                                        }}
                                        getErrors={getErrors}
                                        getValidationState={getValidationState}
                                        isRequired={isRequired}
                                        {...props}
                                    />
                                </div>
                            </div>
                        )}

                        {values.responsibleParty.doesHaveId && (
                            <div className="form-section">
                                <InterviewPersonDocumentation
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    states={lookups.states}
                                    getValidationState={getValidationState}
                                    getErrors={getErrors}
                                    values={values}
                                    fieldBaseName={"responsibleParty.documentInfo"}
                                    documentNumberName={"responsibleParty.documentInfo.documentNumber"}
                                    documentTypeName={"responsibleParty.documentInfo.documentType"}
                                    issuingStateName={"responsibleParty.documentInfo.issuingState"}
                                    documentTypes={lookups.otherDocumentTypes}
                                    isRequired={isRequired}
                                    fullName={getCustomFormattedNameFromObject(
                                        values.responsibleParty,
                                        "the responsible party"
                                    )}
                                />
                            </div>
                        )}

                        <div className="form-section">
                            <div className="form-control auto-input">
                                <YesNoButtons
                                    name={"responsibleParty.isMarried"}
                                    title={`Is ${getCustomFormattedNameFromObject(
                                        values.responsibleParty,
                                        "the responsible party"
                                    )}  married?`}
                                    getErrors={getErrors}
                                    getValidationState={getValidationState}
                                    isRequired={isRequired}
                                    {...props}
                                />
                            </div>
                        </div>
                        <div className="form-section">
                            <div className="form-control auto-input">
                                <YesNoButtons
                                    name={"responsibleParty.isUsCitizen"}
                                    title={`Is ${getCustomFormattedNameFromObject(
                                        values.responsibleParty,
                                        "the responsible party"
                                    )}  a US Citizen?`}
                                    getErrors={getErrors}
                                    getValidationState={getValidationState}
                                    isRequired={isRequired}
                                    {...props}
                                />
                            </div>
                            {values.responsibleParty.isUsCitizen === false && (
                                <div className="form-control l-input">
                                    <Field
                                        name="responsibleParty.usMovingDate"
                                        render={({ field }) => (
                                            <DateInput
                                                {...field}
                                                id="patient.usMovingDate"
                                                title={`Date ${getCustomFormattedNameFromObject(
                                                    values.patient,
                                                    "the patient"
                                                )}  began living in the US`}
                                                changeEvent={v => setFieldValue(field.name, v)}
                                                errorMessage={getErrors(field)}
                                                required={isRequired(field)}
                                                validState={getValidationState(field)}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                        {values.responsibleParty.isUsCitizen === false && (
                            <div className="form-section">
                                <div className="form-control m-input">
                                    <FastField
                                        name="responsibleParty.documentIdType"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.documentIdType"
                                                title={"Document ID Type"}
                                                placeholder={"Document ID Type"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control xxxl-input">
                                    <Field
                                        name="responsibleParty.immigrationStatus"
                                        render={({ field }) => (
                                            <SimpleSingleSelect
                                                {...field}
                                                id={field.name}
                                                title={"Immigration Status"}
                                                setFieldTouched={setFieldTouched}
                                                setFieldValue={setFieldValue}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                options={lookups.immigrationStatus}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control l-input">
                                    <FastField
                                        name="responsibleParty.documentIdNumber"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.documentIdNumber"
                                                title={"Document ID Number"}
                                                placeholder={"Document ID Number"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        {values.responsibleParty.isUsCitizen === false && (
                            <div className="form-section">
                                <div className="form-control l-input">
                                    <FastField
                                        name="responsibleParty.alienOrI94Number"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.alienOrI94Number"
                                                title={"Alien or I-94 Number"}
                                                placeholder={"Alien or I-94 Number"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>

                                <div className="form-control xl-input">
                                    <FastField
                                        name="responsibleParty.cardNumberOrPassportNumber"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.cardNumberOrPassportNumber"
                                                title={"Card Number or Passport Number"}
                                                placeholder={"Card Number or Passport Number"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        {values.responsibleParty.isUsCitizen === false && (
                            <div className="form-section">
                                <div className="form-control l-input">
                                    <FastField
                                        name="responsibleParty.sevisIDOrExpirationDate"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.sevisIDOrExpirationDate"
                                                title={"SEVIS ID or Expiration Date"}
                                                placeholder={"SEVIS ID or Expiration Date"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>

                                <div className="form-control xxl-input">
                                    <FastField
                                        name="responsibleParty.other"
                                        render={({ field }) => (
                                            <InputField
                                                id="responsibleParty.other"
                                                title={"Other (category code or country of issuances)"}
                                                placeholder={"Other (category code or country of issuances)"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}

                        {getAge(values.responsibleParty.dob) > 13 && values.responsibleParty.gender === "Female" && (
                            <PregnancyFields
                                fullName={getCustomFormattedNameFromObject(
                                    values.responsibleParty,
                                    "the responsible party"
                                )}
                                isRequired={isRequired}
                                getErrors={getErrors}
                                getValidationState={getValidationState}
                                fieldSection="responsibleParty"
                            />
                        )}
                        <div className="form-section">
                            <div className="form-control l-input">
                                <FastField
                                    name="responsibleParty.relationshipToPatient"
                                    render={({ field }) => (
                                        <SimpleSingleSelect
                                            {...field}
                                            id={field.name}
                                            title={`Relation to ${getCustomFormattedNameFromObject(
                                                values.patient,
                                                "the patient"
                                            )}`}
                                            setFieldTouched={setFieldTouched}
                                            setFieldValue={setFieldValue}
                                            errorMessage={getErrors(field)}
                                            validState={getValidationState(field)}
                                            options={lookups.relationships}
                                            required={isRequired(field)}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            <WizardButtonsField
                isFirstPage={true}
                navigateToNextPage={navigateToNextPage}
                navigateToPreviousPage={undefined}
                showValidationErrorsOnLoad={encounterStatus === "New"}
            />
        </form>
    );
};
